import { useEffect, useState } from 'react';
import { H2 } from '../../../ui/H2/H2';
import { PlusIcon, XCloseModal } from '../../../../icons/SvgIcons';
import { Input } from '../../../ui/Input/Input';
import { RichTextArea } from '../../../ui/EditableInput/RichTextArea';
import { TextButton } from '../../../ui/Button/Buttons';
import RadioButtonGroup from '../../../ui/RadioButton/RadioButtonGroup';

type ScenarioField = {
  label: string;
  key: string;
  placeholder: string;
};

type LevelOption = {
  label: string;
  key: string;
};

type FundamentalRightsRisksProps = {
  title: string;
  description: string;
  fields: ScenarioField[];
  likelihoodOptions: LevelOption[];
  severityOptions: LevelOption[];
  onChange?: (rows: Record<string, string>[]) => void;
  initialValue?: Record<string, string>[];
};

const LIKELIHOOD_LABEL = 'Likelihood';
const SEVERITY_LABEL = 'Severity';
type ScenarioRow = { id: string; data: Record<string, string> };

export function FundamentalRightsRisks({
  title,
  description,
  fields,
  likelihoodOptions,
  severityOptions,
  onChange,
  initialValue
}: FundamentalRightsRisksProps) {
  const createEmptyRowData = (): Record<string, string> =>
    Object.fromEntries(
      [
        ...fields.map((field) => field.label),
        LIKELIHOOD_LABEL,
        SEVERITY_LABEL
      ].map((label) => [label, ''])
    ) as Record<string, string>;

  const toScenarioRows = (
    source: Record<string, string>[],
    existingRows: ScenarioRow[] = []
  ): ScenarioRow[] =>
    source.map((data, index) => ({
      id: existingRows[index]?.id ?? crypto.randomUUID(),
      data
    }));

  const [rows, setRows] = useState<ScenarioRow[]>(
    initialValue && initialValue.length > 0
      ? toScenarioRows(initialValue)
      : [{ id: crypto.randomUUID(), data: createEmptyRowData() }]
  );

  useEffect(() => {
    if (initialValue && initialValue.length > 0) {
      setRows((prevRows) => toScenarioRows(initialValue, prevRows));
      return;
    }
    setRows([{ id: crypto.randomUUID(), data: createEmptyRowData() }]);
  }, [initialValue]);

  const updateRows = (newRows: ScenarioRow[]) => {
    setRows(newRows);
    onChange?.(newRows.map((row) => row.data));
  };

  const handleChange = (rowId: string, fieldLabel: string, value: string) => {
    updateRows(
      rows.map((row) =>
        row.id === rowId
          ? { ...row, data: { ...row.data, [fieldLabel]: value } }
          : row
      )
    );
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { id: crypto.randomUUID(), data: createEmptyRowData() }
    ]);
  };

  const removeRow = (rowId: string) => {
    const nextRows = rows.filter((row) => row.id !== rowId);
    updateRows(
      nextRows.length > 0
        ? nextRows
        : [{ id: crypto.randomUUID(), data: createEmptyRowData() }]
    );
  };

  const findField = (fieldKey: string) =>
    fields.find((field) => field.key === fieldKey);

  const riskIdField = findField('riskID');
  const fundamentalRightField = findField('fundamentalRight');
  const riskDescriptionField = findField('riskDescription');
  const affectedPersonsField = findField('affectedPersonsGroups');
  const existingControlsField = findField('existingControls');

  return (
    <div className="flex flex-col gap-5">
      {title && <H2 title={title} textSize="text-base" />}
      <p className="text-gray-900">{description}</p>

      {rows.map((row, index) => (
        <div key={row.id} className="flex flex-col gap-4 relative pr-4">
          <div className="flex flex-row items-center justify-between">
            <H2 title={`Scenario ${index + 1}:`} textSize="text-base" />
            <button
              onClick={() => removeRow(row.id)}
              id={`fria-fundamental-rights-risks-remove-row-button-${row.id}`}
              data-testid={`fria-fundamental-rights-risks-remove-row-button-${row.id}`}
              className="absolute right-0 cursor-pointer"
            >
              {rows.length > 1 ? XCloseModal() : null}
            </button>
          </div>

          {riskIdField && fundamentalRightField && (
            <div className="flex gap-4">
              <Input
                id={`${riskIdField.key}-${row.id}`}
                label={riskIdField.label}
                maxLength={255}
                placeholder={riskIdField.placeholder}
                value={row.data[riskIdField.label] || ''}
                onChange={(e) =>
                  handleChange(row.id, riskIdField.label, e.target.value)
                }
                labelClassName="label"
                inputClassName="input"
                containerClassName="flex-1"
              />
              <Input
                id={`${fundamentalRightField.key}-${row.id}`}
                label={fundamentalRightField.label}
                maxLength={255}
                placeholder={fundamentalRightField.placeholder}
                value={row.data[fundamentalRightField.label] || ''}
                onChange={(e) =>
                  handleChange(
                    row.id,
                    fundamentalRightField.label,
                    e.target.value
                  )
                }
                labelClassName="label"
                inputClassName="input"
                containerClassName="flex-1"
              />
            </div>
          )}

          {riskDescriptionField && (
            <RichTextArea
              label={riskDescriptionField.label}
              placeholder={riskDescriptionField.placeholder}
              onChange={(value) =>
                handleChange(row.id, riskDescriptionField.label, value)
              }
              value={row.data[riskDescriptionField.label] || ''}
            />
          )}

          {affectedPersonsField && (
            <Input
              id={`${affectedPersonsField.key}-${row.id}`}
              label={affectedPersonsField.label}
              maxLength={255}
              placeholder={affectedPersonsField.placeholder}
              value={row.data[affectedPersonsField.label] || ''}
              onChange={(e) =>
                handleChange(row.id, affectedPersonsField.label, e.target.value)
              }
              labelClassName="label"
              inputClassName="input"
            />
          )}

          <div className="flex gap-4">
            <div className="flex-1">
              <RadioButtonGroup
                title={LIKELIHOOD_LABEL}
                name={`likelihood-${row.id}`}
                options={likelihoodOptions.map((option) => ({
                  label: option.label,
                  value: option.key
                }))}
                selectedValue={row.data[LIKELIHOOD_LABEL] || ''}
                onChange={(value) =>
                  handleChange(row.id, LIKELIHOOD_LABEL, value)
                }
                direction="horizontal"
              />
            </div>
            <div className="flex-1">
              <RadioButtonGroup
                title={SEVERITY_LABEL}
                name={`severity-${row.id}`}
                options={severityOptions.map((option) => ({
                  label: option.label,
                  value: option.key
                }))}
                selectedValue={row.data[SEVERITY_LABEL] || ''}
                onChange={(value) =>
                  handleChange(row.id, SEVERITY_LABEL, value)
                }
                direction="horizontal"
              />
            </div>
          </div>

          {existingControlsField && (
            <Input
              id={`${existingControlsField.key}-${row.id}`}
              label={existingControlsField.label}
              maxLength={255}
              placeholder={existingControlsField.placeholder}
              value={row.data[existingControlsField.label] || ''}
              onChange={(e) =>
                handleChange(
                  row.id,
                  existingControlsField.label,
                  e.target.value
                )
              }
              labelClassName="label"
              inputClassName="input"
            />
          )}
        </div>
      ))}

      <div className="">
        <TextButton onClick={addRow} icon={PlusIcon()}>
          Add Another
        </TextButton>
      </div>
    </div>
  );
}
